<template>
  <div
    class="vue-block"
    :class="{'selected': selected}"
    :title="name || title"
    :style="blockStyle"
    @click="doubleClick()"
    @touchend="doubleClick()"
  >
    <header class="header font-weight-700 d-flex text-center align-center">
      <div class="number-exts-title elipsis-text">
        {{ blockTitle }}
      </div>
    </header>
    <header v-if="objectType !== 'call flow'" class="number-exts-header d-flex align-center justify-end elipsis-text">
      <div class="elipsis-text" :style="{display: isDeviceOrEmptyDirectory ? 'none' : ''}">
        {{ name }}
      </div>
    </header>
    <div class="inputs height-input text-left d-flex flex-column justify-center">
      <div class="input" v-for="(slot, index) in inputs" :key="index" :style="autoHeight">
        <div class="circle input-color" v-if="slot.active" :class="{active: slot.active, deviceInput: isDeviceOrEmptyDirectory}"></div>
        <div>{{ slot.label }}</div>
      </div>
    </div>
    <div class="outputs text-right d-flex flex-column outputs-height">
      <div class="output" v-for="(slot, index) in outputs" :key="index">
        <div v-if="slot.used" class="circle" :class="{active: slot.active}"></div>
        <div v-if="slot.backLink" :title="slot.backLink" class="back-link elipsis-text text-center" :style="{background: slot.backLinkColor}">
          {{ slot.backLink }}
        </div>
        <div class="output-label elipsis-text">
          <div v-if="['menu', 'queue'].includes(objectType) || (objectType === 'directory' && outputs.length > 1)" class="d-flex flex-row justify-end">
            <span class="menu-name-holder elipsis-text" :title="slot.label">
              {{ slot.label }}
              {{ slot.actionName }}
            </span>
            <span
              v-if="slot.key"
              class="action-key d-inline-flex"
              :class="{ 'queue-key': objectType === 'queue', 'menu-key': objectType === 'menu', 'directory-key': objectType === 'directory' }"
            >
              {{ slot.key }}
            </span>
          </div>
          <div v-else class="elipsis-text">
            <template v-if="slot.actionType">
              <Icon class="icon action-icon" :name="slot.actionType.toLowerCase()"/>
              <span v-if="slot.actionType && !['disconnect', 'directory'].includes(slot.actionType.toLowerCase())" class="actionType">
                {{ slot.actionType === 'receptionist' ? 'Receptionist' : slot.actionType }}:
              </span>
              <span>
                {{ slot.actionName }}
              </span>
            </template>
            <template v-else-if="['device', 'trunk', 'directory'].includes(objectType)">
              <Icon class="icon device-icon" :name="slot.actionName.toLowerCase()"/>
            </template>
            <template v-else>
              <div v-if="slot.label.includes('Preset')" class="font-weight-700 elipsis-text">
                {{ slot.label }}
              </div>
              <div v-else class="font-weight-700 elipsis-text text-left">
                {{ slot.label === 'All Calls' ? `Group: ${slot.label}` : slot.label }}
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Icon from '../icons/Icon.vue';
  import Visualizer from '../helpers/visualizer-helpers';

  export default {
    name: 'VueBlock',
    components: {
      Icon,
    },
    props: {
      x: {
        type: Number,
        default: 0,
        validator(val) {
          return typeof val === 'number';
        }
      },
      y: {
        type: Number,
        default: 0,
        validator(val) {
          return typeof val === 'number';
        }
      },
      selected: Boolean,
      title: {
        type: String,
        default: 'Title'
      },
      inputs: Array,
      outputs: Array,
      color: {
        type: String,
        default: ''
      },
      used: {
        type: Boolean,
        default: false,
      },
      options: {
        type: Object
      },
      actionType: {
        type: String,
        default: ''
      },
      actionName: {
        type: String,
        default: ''
      },
      objectType: {
        type: String,
        default: ''
      },
      name: {
        type: String,
        default: '',
      },
      actionId: {
        type: Number,
        default: -1
      }
    },
    mounted() {
      document.documentElement.addEventListener('mousemove', this.handleMove, true);
      document.documentElement.addEventListener('mousedown', this.handleDown, true);
      document.documentElement.addEventListener('mouseup', this.handleUp, true);
      document.documentElement.addEventListener('touchstart', this.handleDown, { passive: false });
      document.documentElement.addEventListener('touchmove', this.handleMove, { passive: false });
      document.documentElement.addEventListener('touchend', this.handleUp, { passive: false });
    },
    beforeDestroy() {
      document.documentElement.removeEventListener('mousemove', this.handleMove, true);
      document.documentElement.removeEventListener('mousedown', this.handleDown, true);
      document.documentElement.removeEventListener('mouseup', this.handleUp, true);
      document.documentElement.removeEventListener('touchstart', this.handleDown, { passive: false });
      document.documentElement.removeEventListener('touchmove', this.handleMove, { passive: false });
      document.documentElement.removeEventListener('touchend', this.handleUp, { passive: false });
    },
    data() {
      return {
        width: this.$props.options.width,
        dragging: false,
        counter: 0,
        timer: null,
        mouseX: 0,
        mouseY: 0,
        lastMouseX: 0,
        lastMouseY: 0,
        isDeviceOrEmptyDirectory: (['device', 'trunk'].includes(this.$props.objectType) || (this.$props.objectType === 'directory' && this.$props.outputs.length === 1))
      };
    },
    watch: {
      counter() {
        if (this.counter > 1) {
          this.openModal();
        }
      },
    },
    methods: {
      handleMove(e) {
        if (this.dragging) {
          const mouse = Visualizer.getMousePosition(e);
          this.mouseX = mouse.x;
          this.mouseY = mouse.y;

          const diffX = this.mouseX - this.lastMouseX;
          const diffY = this.mouseY - this.lastMouseY;

          this.lastMouseX = this.mouseX;
          this.lastMouseY = this.mouseY;

          this.moveWithDiff(diffX, diffY);
        }
      },
      handleDown(e) {
        const mouse = Visualizer.getMousePosition(e);
        this.mouseX = mouse.x;
        this.mouseY = mouse.y;
        this.lastMouseX = this.mouseX;
        this.lastMouseY = this.mouseY;

        const target = e.target || e.srcElement;
        if (this.$el.contains(target)) {
          this.dragging = true;
          this.$emit('select');

          if (e.preventDefault) e.preventDefault();
        }
      },
      openModal() {
        if (this.$props.actionId >= 0) {
          this.$emit('openModal', {actionId: this.$props.actionId, modal_type: this.$props.objectType});
        }
      },
      doubleClick() {
        this.counter++;
        if (this.counter) {
          this.timer = setTimeout(() => { this.counter = 0; }, 700);
        } else {
          clearTimeout(this.timer);
        }
      },
      handleUp() {
        if (this.dragging) {
          this.dragging = false;
        }
      },
      moveWithDiff(diffX, diffY) {
        const left = this.$props.x + diffX / this.$props.options.scale;
        const top = this.$props.y + diffY / this.$props.options.scale;

        this.$emit('update:x', left);
        this.$emit('update:y', top);
      },
      objectStroke() {
        switch (this.$props.objectType) {
          case 'menu':
            return '1px solid rgba(181, 211, 219, 1)';
          case 'queue':
            return '1px solid rgba(219, 200, 163, 1)';
          case 'preset':
            return '1px solid rgba(70, 112, 53, 0.25)';
          case 'extension':
            return '1px solid rgba(133, 146, 154, 0.25)';
          case 'device':
            return '1px solid rgba(133, 146, 154, 0.25)';
          case 'trunk':
            return '1px solid rgba(133, 146, 154, 0.25)';
          case 'phone_number':
            return '1px solid rgba(210, 219, 189, 1)';
          case 'receptionist':
            return '1px solid rgba(212, 188, 231, 1)';
          case 'directory':
            return '1px solid rgba(138, 197, 183, 1)';
          default:
            return '1px solid rgba(232, 208, 205, 1)';
        }
      }
    },
    computed: {
      blockTitle() {
        if (['device', 'trunk'].includes(this.$props.objectType)) {
          return this.$props.title + (this.$props.name ? ` -  ${this.$props.name}` : '');
        }
        return this.$props.title;
      },
      blockStyle() {
        return {
          top: `${this.$props.options.center.y + this.$props.y * this.$props.options.scale}px`,
          left: `${this.$props.options.center.x + this.$props.x * this.$props.options.scale}px`,
          width: this.isDeviceOrEmptyDirectory ? '151px' : `${this.width}px`,
          transform: `scale(${this.$props.options.scale})`,
          'background': this.$props.color || 'rgba(234, 245, 248, 0.8)',
          'border': this.objectStroke(),
          transformOrigin: 'top left'
        };
      },
      autoHeight() {
        let inputHeight = 32;
        if (this.$props.objectType === 'menu' || this.$props.objectType === 'directory') {
          inputHeight = 28;
        } else if (this.$props.objectType === 'call flow') {
          inputHeight = 0;
        }
        const outputs = this.$props.outputs.length ? this.$props.outputs.length * 21 : 21;
        return {
          'height': `${outputs + inputHeight}px`,
        };
      },
    }
  };
</script>

<style lang="scss" scoped>
$blockBorder: 0px;

$ioPaddingInner: 2px 0;
$ioHeight: 21px;
$ioFontSize: 11px;

$circleBorder: 4px;
$circleSize: 12px;
$circleMargin: 2px; // left/right

$circleNewColor: #364047;;
$circleRemoveColor: #FF0000;
$circleConnectedColor: #FFFF00;

.vue-block {
  position: absolute;
  box-sizing: border-box;
  border: $blockBorder solid black;
  border-radius: 9px;
  color: white;
  border: 1px solid transparent;
  background: white;
  z-index: 1;
  opacity: 0.9;
  cursor: grab;

  &.selected {
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
    z-index: 2;
  }

  > header {
    text-align: center;

    > .delete {
      color: white;
      cursor: pointer;
      float: right;
      position: absolute;
      right: 5px;
      text-decoration: none;
    }
  }
  .inputs, .outputs {
    padding: $ioPaddingInner;
    color: #364047;
    display: block;
    width: 50%;

    > * {
      width: 100%;
    }
  }

  .circle {
    box-sizing: border-box;
    margin-top: $ioHeight / 2 - $circleSize / 2 - 2;

    width: $circleSize;
    height: $circleSize;

    border: $circleBorder solid rgba(54, 64, 71, 1);
    border-radius: 100%;

    // cursor: crosshair;
    background-color: white;
  }

  .inputs {
    float: left;
    width: 8% !important;
    margin-left: -($circleSize/2 + $blockBorder);
  }

  .input, .output {
    height: $ioHeight;
    overflow: hidden;
    font-size: $ioFontSize;
  }

  .input {
    float: left;
    display: flex;
    align-items: center;

    .circle {
      float: left;
      margin-right: $circleMargin;
      margin-left: 2px;
      margin-top: 0px !important;
      width: 5px !important;
      height: 5px !important;

      &:hover {
        background: $circleNewColor;

        &.active {
          background: $circleRemoveColor !important;
        }
      }
    }
  }

  .outputs {
    float: right;
    width: 92% !important;
    margin-right: -7px;
  }

  .output {
    float: right;

    .output-label {
      margin-right: 19px;
      max-height: 20px;
    }
    .circle {
      float: right;
      margin-left: $circleMargin;

      &:hover {
        background: $circleNewColor;
      }
    }
  }
}
.action-key {
  border-radius: 3px;
  width: 18px;
  justify-content: center;
  color: #FFFFFF;
}
.queue-key {
  background: rgb(223, 192, 132);
}
.menu-key, .directory-key {
  background: #2A9BB7;
}
.directory-key {
  min-width: 35px;
}
.icon {
  vertical-align: middle;
  float: left;
  width: 15px;
  height: 15px;
  margin-bottom: 1px;
  margin-right: 5px;
  margin-left: 8px;
  color: #364047;
  z-index: -1;
}
.action-icon {
  position: absolute;
  left: 25px;
}
.device-icon {
  position: absolute;
  width: 50px;
  height: 50px;
  margin-top: -34px;
  margin-left: -90px;
}
.actionType {
  margin-left: 28px;
}
.menu-name-holder {
  margin-right: 7px;
  max-width: 85%;
}
.number-exts-title {
  margin-top: -21px;
  margin-left: 4px;
  font-size: 10px;
}
.number-exts-header {
  color: #364047;
  background-color: transparent !important;
  font-size: 16px;
  width: 90% !important;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
  height: 32px !important;
  float: right;
  border-bottom: none !important;
  margin-right: 9px;
  position: unset !important;
}
.outputs-height {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.height-input {
  padding-bottom: 10px;
}
.input-color {
  background-color: #8C98A0 !important;
  border: none !important;
}
.deviceInput {
  margin-right: -2px !important;
}
.header {
  font-weight: 700;
  color: #364047;
  font-size: 16px;
  height: auto !important;
  border-bottom: none !important;
  background-color: transparent !important;
  position: unset !important;
}
.back-link {
  border: 0.5px solid #8c98a0;
  position: absolute;
  padding-left: 3px;
  padding-right: 3px;
  width: 95px;
  max-height: 18px;
  border-radius: 14px 4px 4px 14px;
  margin-left: 211px;
}
.elipsis-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
